(() => {
  'use strict';
  if (window.KahvemodStockPriority) return;
  const AVAILABILITY = 'filter.v.availability';
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const revisions = new Map();
  const isCollectionUrl = value => {
    const url = new URL(value, location.href);
    return url.origin === location.origin && /\/collections\//.test(url.pathname) && !/\/search(?:\/|$)/.test(url.pathname);
  };
  const isSearch = () => /\/search(?:\/|$)/.test(location.pathname);
  function meta(list) {
    const total = Number(list.dataset.kmTotal), size = Number(list.dataset.kmSize), page = Number(list.dataset.kmPage);
    if (![total, size, page].every(Number.isSafeInteger) || total < 0 || size < 1 || page < 1) {
      throw Error('Invalid Shopify pagination metadata');
    }
    return { total, size, page };
  }
  function ownProducts(list) { return Array.from(list.children).filter(e => e.hasAttribute('data-km-product-id')); }
  function checkCards(list, available, expectedCount) {
    const cards = ownProducts(list);
    if (cards.length !== expectedCount || cards.some(c => !c.dataset.kmProductId ||
        c.dataset.kmAvailable !== String(available)) || new Set(cards.map(c => c.dataset.kmProductId)).size !== cards.length) {
      throw Error('Incomplete or inconsistent Shopify product page');
    }
    return cards;
  }
  function pageParts(total, available, size, page) {
    const start = (page - 1) * size, end = Math.min(start + size, total);
    if (start >= total) return [];
    const parts = [];
    if (start < available) parts.push({ available: true, offset: start, count: Math.min(end, available) - start });
    if (end > available) parts.push({ available: false, offset: Math.max(start - available, 0), count: end - Math.max(start, available) });
    return parts;
  }
  function replaceSlots(list, incoming) {
    const old = ownProducts(list);
    if (old.length !== incoming.length) throw Error('Product slots changed');
    // Keep promotional banners and other non-product children in their original slots.
    old.forEach((node, index) => {
      const next = incoming[index].cloneNode(true);
      next.className = node.className;
      node.replaceWith(next);
    });
  }
  function begin(channel) {
    const revision = (revisions.get(channel) || 0) + 1;
    revisions.set(channel, revision);
    return revision;
  }
  async function orderedCards(value, sectionId, selector, original, channel, requestedRevision) {
    const revision = requestedRevision ?? begin(channel);
    const stillCurrent = () => {
      if (revisions.get(channel) !== revision) throw new DOMException('Superseded render', 'AbortError');
    };
    const base = new URL(value, location.href);
    stillCurrent();
    if (!isCollectionUrl(base)) throw Error('Collection scope required');
    const selected = new Set(base.searchParams.getAll(AVAILABILITY).flatMap(v => v.split(',')));
    // Respect a shopper's explicit single availability filter. No other products are added.
    if (selected.size === 1 && (selected.has('0') || selected.has('1'))) {
      const status = selected.has('1');
      return checkCards(original, status, Math.min(meta(original).size, Math.max(0, meta(original).total - (meta(original).page - 1) * meta(original).size)));
    }
    const native = meta(original);
    if (!native.total) return [];
    const cache = new Map();
    async function read(available, page) {
      const key = `${available}:${page}`;
      if (!cache.has(key)) cache.set(key, (async () => {
        const url = new URL(base);
        url.searchParams.set(AVAILABILITY, available ? '1' : '0');
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw Error('Shopify section request failed');
        const document = parse(await response.text()), list = document.querySelector(selector);
        if (!list) throw Error('Shopify section missing');
        const metadata = meta(list);
        if (metadata.size !== native.size || metadata.page !== page) throw Error('Shopify pagination changed');
        const expected = Math.min(metadata.size, Math.max(0, metadata.total - (page - 1) * metadata.size));
        const cards = checkCards(list, available, expected);
        stillCurrent();
        return { metadata, cards };
      })());
      return cache.get(key);
    }
    const [inStock, outOfStock] = await Promise.all([read(true, 1), read(false, 1)]);
    if (inStock.metadata.total + outOfStock.metadata.total !== native.total) {
      throw Error('Availability filters do not partition this collection');
    }
    const cards = [];
    for (const part of pageParts(native.total, inStock.metadata.total, native.size, native.page)) {
      const first = Math.floor(part.offset / native.size) + 1;
      const last = Math.floor((part.offset + part.count - 1) / native.size) + 1;
      const pages = [];
      for (let page = first; page <= last; page++) pages.push(read(part.available, page));
      const data = await Promise.all(pages);
      const count = part.available ? inStock.metadata.total : outOfStock.metadata.total;
      if (data.some(d => d.metadata.total !== count)) throw Error('Collection changed during render');
      cards.push(...data.flatMap(d => d.cards).slice(part.offset % native.size, part.offset % native.size + part.count));
    }
    const expected = Math.min(native.size, Math.max(0, native.total - (native.page - 1) * native.size));
    if (cards.length !== expected || new Set(cards.map(c => c.dataset.kmProductId)).size !== cards.length) {
      throw Error('Combined product page is incomplete');
    }
    stillCurrent();
    return cards;
  }
  function ready(host) {
    host.dataset.kmState = 'ready';
    host.removeAttribute('aria-busy');
    host.querySelector('[data-km-status]')?.remove();
  }
  function showError(host, retry, hide = true) {
    if (!host?.isConnected) return;
    host.dataset.kmState = hide ? 'error' : 'ready';
    host.removeAttribute('aria-busy');
    host.querySelector('[data-km-status]')?.remove();
    const status = document.createElement('div');
    status.setAttribute('data-km-status', ''); status.setAttribute('role', 'alert');
    const message = document.createElement('p'); message.textContent = 'Ürünler yüklenemedi.';
    const button = document.createElement('button'); button.type = 'button'; button.className = 'btn btn--secondary';
    button.textContent = 'Yeniden dene'; button.addEventListener('click', retry, { once: true });
    status.append(message, button); host.prepend(status);
  }
  async function transform(html, value, revision) {
    if (!isCollectionUrl(value) || isSearch()) return html;
    const document = parse(html), list = document.querySelector('#ProductsList[data-km-section]');
    if (!list) throw Error('Stock priority markup missing');
    const incoming = await orderedCards(value, list.dataset.kmSection, '#ProductsList[data-km-section]', list, 'collection', revision);
    replaceSlots(list, incoming);
    const grid = document.getElementById('ProductGridContainer');
    grid.dataset.kmState = 'ready'; grid.removeAttribute('aria-busy'); grid.querySelector('[data-km-status]')?.remove();
    return document.documentElement.outerHTML;
  }
  async function initializeCollection() {
    if (!isCollectionUrl(location.href) || isSearch()) return;
    const host = document.getElementById('ProductGridContainer');
    if (!host || host.dataset.kmState === 'ready') return;
    host.dataset.kmState = 'pending'; host.setAttribute('aria-busy', 'true');
    const initialUrl = location.href;
    try {
      const html = await transform(document.documentElement.outerHTML, initialUrl);
      if (!host.isConnected || location.href !== initialUrl) return;
      const source = parse(html).getElementById('ProductGridContainer');
      host.innerHTML = source.innerHTML;
      ready(host);
      document.dispatchEvent(new CustomEvent('collection:rerendered'));
    } catch (e) {
      if (e.name !== 'AbortError') showError(host, initializeCollection);
    }
  }
  async function prioritizeFeatured(host) {
    if (isSearch() || !host.dataset.kmCollectionUrl) { ready(host); return; }
    host.dataset.kmState = 'pending'; host.setAttribute('aria-busy', 'true');
    const list = host.querySelector('.featured-collection__items');
    const url = new URL(host.dataset.kmCollectionUrl, location.href);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.set('view', 'kahvemod-stock-feed');
    const cards = await orderedCards(url, host.dataset.kmSection, '[data-km-featured-feed]', list, 'featured:' + host.id);
    if (!host.isConnected) throw new DOMException('Detached featured collection', 'AbortError');
    replaceSlots(list, cards); ready(host);
  }
  window.KahvemodStockPriority = { transform, prioritizeFeatured, showError, isCollectionUrl, pageParts, initializeCollection,
    beginCollection: () => begin('collection'), isCurrentCollection: revision => revisions.get('collection') === revision, markReady: ready };
  document.dispatchEvent(new CustomEvent('kahvemod:stock-ready'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeCollection, { once: true });
  else queueMicrotask(initializeCollection);
})();
